/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
  ----------------------------------------------------------------------------------------
  Item Name: Vuesax Admin - VueJS Dashboard Admin Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/
import Vue from "vue";
import Router from "vue-router";

Vue.use(Router);

const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  scrollBehavior() {
    return {
      x: 0,
      y: 0
    };
  },
  routes: [
    {
      // =============================================================================
      // MAIN LAYOUT ROUTES
      // =============================================================================
      path: "",
      component: () => import("./layouts/main/Main.vue"),
      children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================
        {
          path: "/",
          name: "home",
          component: () => import("./views/Home.vue"),
          meta: {
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/dashboard",
          name: "dashboard",
          component: () => import("./views/components/Dashboard.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "HOME"
              },
              {
                title: "Summary",
                active: true
              }
            ],
            pageTitle: "Summary",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/users",
          name: "users",
          component: () => import("./views/components/users/usersmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "จัดการสมาชิก"
              },
              {
                title: "จัดการข้อมูลสมาชิก",
                active: true
              }
            ],
            pageTitle: "จัดการข้อมูลสมาชิก",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/adminusers",
          name: "adminusers",
          component: () =>
            import("./views/components/usersadmin/usersmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "ผู้ดูและระบบ"
              },
              {
                title: "จัดการข้อมูลสมาชิก",
                active: true
              }
            ],
            pageTitle: "จัดการข้อมูลสมาชิก",
            rule: "admindto"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/adminagents",
          name: "adminagents",
          component: () => import("./views/components/agents/agentsmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "ผู้ดูและระบบ"
              },
              {
                title: "จัดการข้อมูล Agent",
                active: true
              }
            ],
            pageTitle: "จัดการข้อมูล Agent",
            rule: "masteradmin"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/banks",
          name: "banks",
          component: () => import("./views/components/banks/banksmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "จัดการข้อมูลธนาคาร"
              },
              {
                title: "ธนาคารหลัก",
                active: true
              }
            ],
            pageTitle: "ธนาคารหลัก",
            rule: "masteradmin"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/adminroles",
          name: "adminroles",
          component: () => import("./views/components/roles/rolesmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "ผู้ดูและระบบ"
              },
              {
                title: "จัดการสิทธิ",
                active: true
              }
            ],
            pageTitle: "จัดการสิทธิ",
            rule: "mastereditor"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/adminsyslogs",
          name: "adminsyslogs",
          component: () => import("./views/components/syslogs/logsmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "ผู้ดูแลระบบ"
              },
              {
                title: "Admin Logs",
                active: true
              }
            ],
            pageTitle: "Admin Logs",
            rule: "editor"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/adminsyslogsreport",
          name: "adminsyslogsreport",
          component: () => import("./views/components/syslogsreport/syslogsreport_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "ผู้ดูแลระบบ"
              },
              {
                title: "Admin Logs",
                active: true
              }
            ],
            pageTitle: "Admin Logs",
            rule: "editor"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/qconfig",
          name: "qconfig",
          component: () => import("./views/components/qconfig/qmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "Queue"
              },
              {
                title: "QueueConfig",
                active: true
              }
            ],
            pageTitle: "QueueConfig",
            rule: "masteradmin"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/qcustomer",
          name: "qcustomer",
          component: () => import("./views/components/qcustomer/qmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "Queue"
              },
              {
                title: "QueueCustomer",
                active: true
              }
            ],
            pageTitle: "QueueCustomer",
            rule: "masteradmin"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/qinv",
          name: "qinv",
          component: () => import("./views/components/qinv/qmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "Queue"
              },
              {
                title: "QueueInvoiceRec",
                active: true
              }
            ],
            pageTitle: "QueueInvoiceRec",
            rule: "masteradmin"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/qrec",
          name: "qrec",
          component: () => import("./views/components/qrec/qmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "Queue"
              },
              {
                title: "QueueReceiptRec",
                active: true
              }
            ],
            pageTitle: "QueueReceiptRec",
            rule: "masteradmin"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/qdownloadice",
          name: "qdownloadice",
          component: () => import("./views/components/qdownloadice/qmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "Queue"
              },
              {
                title: "QueueDownloadIce",
                active: true
              }
            ],
            pageTitle: "QueueDownloadIce",
            rule: "masteradmin"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/fdmsexb",
          name: "fdmsexb",
          component: () => import("./views/components/fdmsexb/exbdmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "FDMS"
              },
              {
                title: "OutboundService",
                active: true
              }
            ],
            pageTitle: "OutboundService",
            rule: "editor"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/fdmsinb",
          name: "fdmsinb",
          component: () => import("./views/components/fdmsinb/inbdmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "FDMS"
              },
              {
                title: "InboundService",
                active: true
              }
            ],
            pageTitle: "InboundService",
            rule: "editor"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/exptypes",
          name: "exptypes",
          component: () => import("./views/components/exptypes/expmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "Export Type",
                active: true
              }
            ],
            pageTitle: "Export Type",
            rule: "admin"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/transmodes",
          name: "transmodes",
          component: () =>
            import("./views/components/transmodes/transmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "Transport Mode",
                active: true
              }
            ],
            pageTitle: "Transport Mode",
            rule: "admin"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/protypes",
          name: "protypes",
          component: () => import("./views/components/protypes/promain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "Product Type",
                active: true
              }
            ],
            pageTitle: "Product Type",
            rule: "admin"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/zones",
          name: "zones",
          component: () => import("./views/components/zones/zonesmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "Zone",
                active: true
              }
            ],
            pageTitle: "Zone",
            rule: "editor"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/warehouses",
          name: "warehouses",
          component: () =>
            import("./views/components/warehouses/warehousesmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "Warehouse",
                active: true
              }
            ],
            pageTitle: "Warehouse",
            rule: "editor"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/countrys",
          name: "countrys",
          component: () =>
            import("./views/components/countrys/countrysmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "Country",
                active: true
              }
            ],
            pageTitle: "Country",
            rule: "editor"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/ports",
          name: "ports",
          component: () => import("./views/components/ports/portsmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "Port",
                active: true
              }
            ],
            pageTitle: "Port",
            rule: "admin"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/customsports",
          name: "customsports",
          component: () =>
            import("./views/components/customsports/custmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "CustomsPort",
                active: true
              }
            ],
            pageTitle: "CustomsPort",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/companygroups",
          name: "companygroups",
          component: () =>
            import("./views/components/companygroups/compmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "CompanyGroup",
                active: true
              }
            ],
            pageTitle: "CompanyGroup",
            rule: "admin"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/businesspartners",
          name: "businesspartners",
          component: () =>
            import("./views/components/businesspartners/busmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "BusinessPartner",
                active: true
              }
            ],
            pageTitle: "BusinessPartner",
            rule: "editor"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/shippings",
          name: "shippings",
          component: () =>
            import("./views/components/shippings/busmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "Shipping",
                active: true
              }
            ],
            pageTitle: "Shipping",
            rule: "editor"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/packages",
          name: "packages",
          component: () => import("./views/components/packages/packmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "Package",
                active: true
              }
            ],
            pageTitle: "Package",
            rule: "editor"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/sections",
          name: "sections",
          component: () => import("./views/components/sections/secmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "Section",
                active: true
              }
            ],
            pageTitle: "Section",
            rule: "editor"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/locations",
          name: "locations",
          component: () => import("./views/components/locations/locmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "Location",
                active: true
              }
            ],
            pageTitle: "Location",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/processindicators",
          name: "processindicators",
          component: () =>
            import("./views/components/processindicators/promain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "ProcessIndicator",
                active: true
              }
            ],
            pageTitle: "ProcessIndicator",
            rule: "admin"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/cartypes",
          name: "cartypes",
          component: () => import("./views/components/cartypes/carmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "CarType",
                active: true
              }
            ],
            pageTitle: "CarType",
            rule: "admin"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/messagefunctions",
          name: "messagefunctions",
          component: () =>
            import("./views/components/messagefunctions/messmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "MessageFunction",
                active: true
              }
            ],
            pageTitle: "MessageFunction",
            rule: "admin"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/provinces",
          name: "provinces",
          component: () => import("./views/components/provinces/provmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "Province",
                active: true
              }
            ],
            pageTitle: "Province",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/tmotypes",
          name: "tmotypes",
          component: () => import("./views/components/tmotypes/tmotmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "TmoType",
                active: true
              }
            ],
            pageTitle: "TmoType",
            rule: "admin"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/constates",
          name: "constates",
          component: () => import("./views/components/constates/consmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "ContainerStateCode",
                active: true
              }
            ],
            pageTitle: "ContainerStateCode",
            rule: "admin"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/contypesize",
          name: "contypesize",
          component: () =>
            import("./views/components/contypesize/contmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "ContainerTypeSize",
                active: true
              }
            ],
            pageTitle: "ContainerTypeSize",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/cargomovements",
          name: "cargomovements",
          component: () =>
            import("./views/components/cargomovements/cargo_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "CargoMovement",
                active: true
              }
            ],
            pageTitle: "CargoMovement",
            rule: "admin"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/goodscargotypes",
          name: "goodscargotypes",
          component: () =>
            import("./views/components/goodscargotypes/cargo_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "GoodsCargoType",
                active: true
              }
            ],
            pageTitle: "GoodsCargoType",
            rule: "admin"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/tmoins",
          name: "tmoins",
          component: () => import("./views/components/tmoins/inbmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "DTOImport"
              },
              {
                title: "Order",
                active: true
              }
            ],
            pageTitle: "DTOImport",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/inbound",
          name: "inbound",
          component: () => import("./views/components/inbound/inbmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Inbound"
              },
              {
                title: "Order",
                active: true
              }
            ],
            pageTitle: "Inbound",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/DTOCMANImport",
          name: "DTOCMANImport",
          component: () =>
            import("./views/components/dtocmans/cmansim_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "DTO CMAN Import"
              },
              {
                title: "Order",
                active: true
              }
            ],
            pageTitle: "DTO CMAN Import",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/cmans",
          name: "cmans",
          component: () =>
            import("./views/components/cmans/cmansim_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "CMAN Import"
              },
              {
                title: "Order",
                active: true
              }
            ],
            pageTitle: "CMAN Import",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/CMANExport",
          name: "CMANExport",
          component: () =>
            import("./views/components/dtocmans/cmansex_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "DTO CMAN Export"
              },
              {
                title: "Order",
                active: true
              }
            ],
            pageTitle: "DTO CMAN Export",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/cmansexsdetails/:id",
          name: "cmansexsdetails",
          component: () =>
            import("./views/components/cmansexsdetails/cmansdetails_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Outbound"
              },
              {
                title: "By Truck",
                url: "/cmansexs"
              },
              {
                title: "Details",
                active: true
              }
            ],
            pageTitle: "Details",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/listfdetails/:id",
          name: "listfdetails",
          component: () =>
            import("./views/components/listf/listfdetails_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Outbound"
              },
              {
                title: "Order",
                url: "/outbound"
              },
              {
                title: "Details",
                active: true
              }
            ],
            pageTitle: "Details",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/DTOCmansdetails/:id",
          name: "DTOCmansdetails",
          component: () =>
            import("./views/components/dtocmansdetails/cmansdetails_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "DTO CMAN Import"
              },
              {
                title: "DTO CMAN Details",
                active: true
              }
            ],
            pageTitle: "DTO CMAN Details",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/CmansdetailsMTMS/:id",
          name: "CmansdetailsMTMS",
          component: () =>
            import("./views/components/cmansdetails/cmansdetailsmtms_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "CMAN",
                url: "/cmans"
              },
              {
                title: "CMANDetails",
                active: true
              }
            ],
            pageTitle: "CMANdetails",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/putawaysdetails/:id",
          name: "putawaysdetails",
          component: () =>
            import("./views/components/putawaysdetails/putdmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "PutAway",
                url: "/putaway"
              },
              {
                title: "Putawaysdetails",
                active: true
              }
            ],
            pageTitle: "Putawaysdetails",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/pickdetails/:id",
          name: "pickdetails",
          component: () =>
            import("./views/components/pickdetails/pickdmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Outbound",
              },
              {
                title: "Picking",
                url: "/picking"
              },
              {
                title: "Pickdetails",
                active: true
              }
            ],
            pageTitle: "Pickdetails",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/picking",
          name: "picking",
          component: () => import("./views/components/picking/pickmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Outbound",
              },
              {
                title: "Picking",
                active: true
              }
            ],
            pageTitle: "Picking",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/ships",
          name: "ships",
          component: () => import("./views/components/ships/shipsmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Outbound",
              },
              {
                title: "Ships",
                active: true
              }
            ],
            pageTitle: "Ships",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/putaway",
          name: "putaway",
          component: () => import("./views/components/putaway/putmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Inbound"
              },
              {
                title: "Putaways",
                active: true
              }
            ],
            pageTitle: "Putaways",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/inbtracking",
          name: "inbtracking",
          component: () =>
            import("./views/components/inbtracking/inbtracking_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "InboundTracking"
              },
              {
                title: "Main",
                active: true
              }
            ],
            pageTitle: "InboundTracking",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/fromdoms",
          name: "fromdoms",
          component: () => import("./views/components/fromdoms/inbmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Inbound"
              },
              {
                title: "From DOM",
                active: true
              }
            ],
            pageTitle: "From DOM",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/dtofromdoms",
          name: "dtofromdoms",
          component: () => import("./views/components/dtofromdoms/inbmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Inbound"
              },
              {
                title: "DTO From DOM",
                active: true
              }
            ],
            pageTitle: "DTO From DOM",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/dtofromdomsdetails/:id",
          name: "dtofromdomsdetails",
          component: () =>
            import("./views/components/dtofromdomsdetails/inbdmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "DTO From DOM",
                url: "/dtofromdoms"
              },
              {
                title: "DTO From Dom Details",
                active: true
              }
            ],
            pageTitle: "DTO From DOM Inbdetails",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/dominsdetails/:id",
          name: "dominsdetails",
          component: () =>
            import("./views/components/dominsdetails/inbdmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "From DOM",
                url: "/domins"
              },
              {
                title: "From Dom Details",
                active: true
              }
            ],
            pageTitle: "From DOM Inbdetails",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/sabs",
          name: "sabs",
          component: () => import("./views/components/sabs/sabsin_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Inbound"
              },
              {
                title: "SeaAirImport",
                active: true
              }
            ],
            pageTitle: "SeaAirImport",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/dtosabs",
          name: "dtosabs",
          component: () => import("./views/components/dtosabs/sabsin_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "DTO Function"
              },
              {
                title: "DTO SeaAirImport",
                active: true
              }
            ],
            pageTitle: "DTO SeaAirImport",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/dtosabsdetails/:id",
          name: "dtosabsdetails",
          component: () =>
            import("./views/components/dtosabsdetails/sabsdmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "DTO SeaAirImport",
                url: "/dtosabs"
              },
              {
                title: "DTO SeaAirDetail",
                active: true
              }
            ],
            pageTitle: "DTO SeaAirDetail",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/sabsdetails/:id",
          name: "sabsdetails",
          component: () =>
            import("./views/components/sabsdetails/sabsdmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "SeaAirImport",
                url: "/sabs"
              },
              {
                title: "SeaAirDetail",
                active: true
              }
            ],
            pageTitle: "SeaAirDetail",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/BorderExport",
          name: "BorderExport",
          component: () => import("./views/components/sabs/sabsex_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "OUTBOUND"
              },
              {
                title: "Border Export",
                active: true
              }
            ],
            pageTitle: "Border Export",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/DTOBorderExport",
          name: "DTOBorderExport",
          component: () => import("./views/components/dtosabs/sabsex_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "DTO Function"
              },
              {
                title: "DTO Border Export",
                active: true
              }
            ],
            pageTitle: "DTO Border Export",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/dtoexbdetails/:id",
          name: "dtoexbdetails",
          component: () =>
            import("./views/components/dtoexbdetails/exbdmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "DTO Function",
                url: "/DTOBorderExport"
              },
              {
                title: "Exbdetail",
                active: true
              }
            ],
            pageTitle: "To Border",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/exbdetails/:id",
          name: "exbdetails",
          component: () =>
            import("./views/components/exbdetails/exbdmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Outbound",
              },
              {
                title: "Exbdetail",
                active: true
              }
            ],
            pageTitle: "Exbdetail",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/cpermitdetails/:id",
          name: "cpermitdetails",
          component: () =>
            import("./views/components/cpermitdetails/exbdmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Outbound",
              },
              {
                title: "Customs Permit",
              },
              {
                title: "Detail",
                active: true
              }
            ],
            pageTitle: "Detail",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/customspermitdetails/:id",
          name: "customspermitdetails",
          component: () =>
            import("./views/components/customspermitdetails/inbdmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Outbound",
              },
              {
                title: "Customs Permit API",
                url: "/customspermit"
              },
              {
                title: "Detail",
                active: true
              }
            ],
            pageTitle: "Detail",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/cmansexs",
          name: "cmansexs",
          component: () => import("./views/components/cmansexs/cmansex_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Outbound"
              },
              {
                title: "By Truck",
                active: true
              }
            ],
            pageTitle: "By Truck",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/ToDom",
          name: "ToDom",
          component: () => import("./views/components/todoms/exbmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Outbound"
              },
              {
                title: "To Dom",
                active: true
              }
            ],
            pageTitle: "To Dom",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/DTOToDom",
          name: "DTOToDom",
          component: () => import("./views/components/dtotodoms/exbmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "DTO Function"
              },
              {
                title: "DTO To Dom",
                active: true
              }
            ],
            pageTitle: "DTO To Dom",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/dtotodomsdetails/:id",
          name: "dtotodomsdetails",
          component: () =>
            import("./views/components/dtotodomsdetails/exbdmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "DTO Function",
                url: "/DTOToDom"
              },
              {
                title: "Exbdetail",
                active: true
              }
            ],
            pageTitle: "To DOM",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/todomsdetails/:id",
          name: "todomsdetails",
          component: () =>
            import("./views/components/todomsdetails/exbdmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "To DOM",
                url: "/ToDom"
              },
              {
                title: "Exbdetail",
                active: true
              }
            ],
            pageTitle: "To DOM",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/handlingcharges",
          name: "handlingcharges",
          component: () =>
            import("./views/components/handlingcharges/busmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Billing",
              },
              {
                title: "HandlingCharge",
                active: true
              }
            ],
            pageTitle: "HandlingCharge",
            rule: "editor"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/inbhandlings",
          name: "inbhandlings",
          component: () =>
            import("./views/components/inbhandlings/inbmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Billing",
              },
              {
                title: "HandlingMap",
                active: true
              }
            ],
            pageTitle: "HandlingMap",
            rule: "editor"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/invoices",
          name: "invoices",
          component: () =>
            import("./views/components/invoices/invmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Billing",
              },
              {
                title: "Invoice",
                active: true
              }
            ],
            pageTitle: "Invoice",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/invoicesrec",
          name: "invoicesrec",
          component: () =>
            import("./views/components/invoicesrec/invmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Billing",
              },
              {
                title: "InvoiceReconcile",
                active: true
              }
            ],
            pageTitle: "InvoiceReconcile",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/receiptsrec",
          name: "receiptsrec",
          component: () =>
            import("./views/components/receiptsrec/recmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Billing",
              },
              {
                title: "ReceiptsReconcile",
                active: true
              }
            ],
            pageTitle: "ReceiptsReconcile",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/cninvoicesrec",
          name: "cninvoicesrec",
          component: () =>
            import("./views/components/cninvoicesrec/invmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Billing",
              },
              {
                title: "CreditNoteReconcile",
                active: true
              }
            ],
            pageTitle: "CreditNoteReconcile",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/cninvoices",
          name: "cninvoices",
          component: () =>
            import("./views/components/cninvoices/cninvmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Billing",
              },
              {
                title: "CreditNote",
                active: true
              }
            ],
            pageTitle: "CreditNote",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        // {
        //   path: "/cninvoicesnorcv",
        //   name: "cninvoicesnorcv",
        //   component: () =>
        //     import("./views/components/cninvoicesnorcv/cninvmain.vue"),
        //   meta: {
        //     breadcrumb: [
        //       {
        //         title: "Home",
        //         url: "/"
        //       },
        //       {
        //         title: "Billing",
        //       },
        //       {
        //         title: "CreditNote Fix Area",
        //         active: true
        //       }
        //     ],
        //     pageTitle: "CreditNote Fix Area",
        //     rule: "user"
        //   },
        //   beforeEnter(to, from, next) {
        //     if (!localStorage.getItem("KK_AUTH_TOKEN")) {
        //       next("/pages/login");
        //     } else {
        //       next();
        //     }
        //   }
        // },
        {
          path: "/cninvoiceapp",
          name: "cninvoiceapp",
          component: () =>
            import("./views/components/cninvoiceapp/cninvmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Billing",
              },
              {
                title: "CreditNote Approve",
                active: true
              }
            ],
            pageTitle: "CreditNote Approve",
            rule: "editor"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/cninvoicesdetails/:id",
          name: "cninvoicesdetails",
          component: () =>
            import("./views/components/cninvoicesdetails/invdmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Billing",
              },
              {
                title: "CreditNote",
                url: "/cninvoices"
              },
              {
                title: "CreditNote Detail",
                active: true
              }
            ],
            pageTitle: "CreditNote Detail",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/invoicesdetails/:id",
          name: "invoicesdetails",
          component: () =>
            import("./views/components/invoicesdetails/invdmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Billing",
              },
              {
                title: "Invoice",
                url: "/invoices"
              },
              {
                title: "Invoice Detail",
                active: true
              }
            ],
            pageTitle: "Invoice Detail",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/billingresponse/:id",
          name: "billingresponse",
          component: () =>
            import("./views/components/billingresponse/invdmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Billing",
              },
              {
                title: "Response Detail",
                active: true
              }
            ],
            pageTitle: "Response Detail",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/dtotmoexs",
          name: "dtotmoexs",
          component: () =>
            import("./views/components/dtotmoexs/exmain.vue"),
          meta: {
            breadcrumb: [{
              title: 'Home',
              url: '/'
            },
            {
              title: 'DTO Function',
            },
            {
              title: 'Movement Z3<>Z1',
            },
            {
              title: 'DTOExport',
              active: true
            },
            ],
            pageTitle: "Z3-Z1",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/dtotmoins",
          name: "dtotmoins",
          component: () =>
            import("./views/components/dtotmoins/inbmain.vue"),
          meta: {
            breadcrumb: [{
              title: 'Home',
              url: '/'
            },
            {
              title: 'DTO Function',
            },
            {
              title: 'Movement Z1<>Z3',
            },
            {
              title: 'DTOImport',
              active: true
            },
            ],
            pageTitle: "Z1-Z3",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/dtotmoinsdetails/:id",
          name: "dtotmoinsdetails",
          component: () =>
            import("./views/components/dtotmoinsdetails/inbdmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: 'DTO Function',
              },
              {
                title: 'Movement Z1<>Z3',
              },
              {
                title: 'Inbound',
                url:'/dtotmoins'
              },
              {
                title: 'Details',
                active: true
              },
            ],
            pageTitle: "Z1-Z3 Detail",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/dtotmoinsdetails_inbdetails/:id",
          name: "dtotmoinsdetails_inbdetails",
          component: () =>
            import("./views/components/dtotmoinsdetails/inbdmain_inbdetails.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: 'DTO Function',
              },
              {
                title: 'Movement Z1<>Z3',
              },
              {
                title: 'Inbound',
                url:'/dtotmoins'
              },
              {
                title: 'Details',
                active: true
              },
            ],
            pageTitle: "Z1-Z3 Detail",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/dtotmoexsdetails_order/:id",
          name: "dtotmoexsdetails_order",
          component: () =>
            import("./views/components/dtotmoexsdetails_order/exdmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: 'To TMO',
                url:'/tmoexs'
              },
              {
                title: 'Details',
                active: true
              },
            ],
            pageTitle: "Z3-Z1 Detail",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/tmoexs",
          name: "tmoexs",
          component: () =>
            import("./views/components/tmoexs/exbmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Outbound"
              },
              {
                title: "To TMO",
                active: true
              }
            ],
            pageTitle: "To TMO",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/cpermits",
          name: "cpermits",
          component: () => import("./views/components/cpermits/permits_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Outbound"
              },
              {
                title: "Customs Permit Manual",
                active: true
              }
            ],
            pageTitle: "Customs Permit Manual",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/customspermit",
          name: "customspermit",
          component: () => import("./views/components/customspermit/permits_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Outbound"
              },
              {
                title: "Customs Permit API",
                active: true
              }
            ],
            pageTitle: "Customs Permit API",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/outbound",
          name: "outbound",
          component: () => import("./views/components/outbound/exbmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Outbound"
              },
              {
                title: "Order",
                active: true
              }
            ],
            pageTitle: "Order",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/dtomoves",
          name: "dtomoves",
          component: () =>
            import("./views/components/dtomoves/movemain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "DTO Function"
              },
              {
                title: "Move"
              },
              {
                title: "DTO-DTO",
                active: true
              }
            ],
            pageTitle: "DTO-DTO",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: '/dtomovesdetails/:id',
          name: 'dtomovesdetails',
          component: () => import('./views/components/dtomovesdetails/movedmain.vue'),
          meta: {
            breadcrumb: [{
              title: 'Home',
              url: '/'
            },
            {
              title: 'DTO Move',
              url: '/dtomoves'
            },
            {
              title: 'DTO Move Detail',
              active: true
            },
            ],
            pageTitle: 'DTO Move Detail',
            rule: 'user'
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/reportoutboundall",
          name: "reportoutboundall",
          component: () => import("./views/components/reports/outbound/outboundreport_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "รายงาน"
              },
              {
                title: "Outbound"
              },
              {
                title: "Outbound All",
                active: true
              }
            ],
            pageTitle: "Outbound All",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/reportinboundall",
          name: "reportinboundall",
          component: () => import("./views/components/reports/inbound/inboundreport_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "รายงาน"
              },
              {
                title: "Inbound"
              },
              {
                title: "Inbound All",
                active: true
              }
            ],
            pageTitle: "Inbound All",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/reportinboundcargo",
          name: "reportinboundcargo",
          component: () => import("./views/components/reports/inbound_cargo/inboundreport_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "รายงาน"
              },
              {
                title: "Inbound"
              },
              {
                title: "Inbound Cargo",
                active: true
              }
            ],
            pageTitle: "Inbound Cargo",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/reportinboundtmo",
          name: "reportinboundtmo",
          component: () => import("./views/components/reports/inbound_tmo/inboundreport_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "รายงาน"
              },
              {
                title: "Inbound"
              },
              {
                title: "Inbound TMO",
                active: true
              }
            ],
            pageTitle: "Inbound TMO",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/reportoutboundtmo",
          name: "reportoutboundtmo",
          component: () => import("./views/components/reports/outbound_tmo/outboundreport_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "รายงาน"
              },
              {
                title: "Outbound"
              },
              {
                title: "Outbound TMO",
                active: true
              }
            ],
            pageTitle: "Outbound TMO",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/reportoutboundcargo",
          name: "reportoutboundcargo",
          component: () => import("./views/components/reports/outbound_cargo/outboundreport_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "รายงาน"
              },
              {
                title: "Outbound"
              },
              {
                title: "Outbound Cargo",
                active: true
              }
            ],
            pageTitle: "Outbound Cargo",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/reportmovements",
          name: "reportmovements",
          component: () => import("./views/components/reports/z1z3/zreport_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "รายงาน"
              },
              {
                title: "Z1Z3"
              },
              {
                title: "Z1Z3 Report",
                active: true
              }
            ],
            pageTitle: "Z1Z3 Report",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/balanceReport",
          name: "balanceReport",
          component: () => import("./views/components/reports/balances/balancesreport_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "รายงาน"
              },
              {
                title: "Balance"
              },
              {
                title: "Balance Report",
                active: true
              }
            ],
            pageTitle: "Balance Report",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/invoicesReport",
          name: "invoicesReport",
          component: () => import("./views/components/reports/invoices/invreport_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "รายงาน"
              },
              {
                title: "Invoice"
              },
              {
                title: "Invoice Report",
                active: true
              }
            ],
            pageTitle: "Invoice Report",
            rule: "editor"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/receiptsReport",
          name: "receiptsReport",
          component: () => import("./views/components/reports/receipts/recreport_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "รายงาน"
              },
              {
                title: "Receipt"
              },
              {
                title: "Receipt Report",
                active: true
              }
            ],
            pageTitle: "Receipt Report",
            rule: "editor"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/creditnoteReport",
          name: "creditnoteReport",
          component: () => import("./views/components/reports/creditnotes/cnreport_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "รายงาน"
              },
              {
                title: "CreditNote"
              },
              {
                title: "CreditNote Report",
                active: true
              }
            ],
            pageTitle: "CreditNote Report",
            rule: "editor"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/volumeReport",
          name: "volumeReport",
          component: () => import("./views/components/reports/volumereport/volumereport_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "รายงาน"
              },
              {
                title: "Volume"
              },
              {
                title: "Volume Report",
                active: true
              }
            ],
            pageTitle: "Volume Report",
            rule: "editor"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/dlvorders",
          name: "dlvorders",
          component: () => import("./views/components/dlvorders/dlvmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Delivery Order"
              },
              {
                title: "Delivery Order"
              },
              {
                title: "Delivery Order Component",
                active: true
              }
            ],
            pageTitle: "Delivery Order Component",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/lista",
          name: "lista",
          component: () => import("./views/components/lista/lista_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Overtime Goods"
              },
              {
                title: "ListA"
              },
              {
                title: "ListA Component",
                active: true
              }
            ],
            pageTitle: "ListA Component",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/listfsubmit",
          name: "listfsubmit",
          component: () => import("./views/components/listf/pgrid.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Overtime Goods"
              },
              {
                title: "list F"
              },
              {
                title: "list F Submit",
                active: true
              }
            ],
            pageTitle: "list F Submit",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/listf",
          name: "listf",
          component: () => import("./views/components/listf/listf_main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Overtime Goods"
              },
              {
                title: "list F"
              },
              {
                title: "list F Component",
                active: true
              }
            ],
            pageTitle: "list F Component",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/Bots",
          name: "Bots",
          component: () => import("./views/components/bots/botsmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ระบบ Auto"
              },
              {
                title: "ระบบ Bot Auto",
                active: true
              }
            ],
            pageTitle: "ระบบ Bot Auto",
            rule: "admin"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/reportadmin",
          name: "reportadmin",
          component: () =>
            import("./views/components/reportadmin/reportadminmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ระบบรายงาน",
                active: true
              }
            ],
            pageTitle: "ระบบรายงาน",
            rule: "customs"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/chrequests",
          name: "chrequests",
          component: () =>
            import("./views/components/chrequests/chmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ChangeRequest"
              },
              {
                title: "Main",
                active: true
              }
            ],
            pageTitle: "Main",
            rule: "editor"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/receipts",
          name: "receipts",
          component: () => import("./views/components/receipts/recmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Billing",
              },
              {
                title: "Receipt",
                active: true
              }
            ],
            pageTitle: "Receipt",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/receiptsdetails/:id",
          name: "receiptsdetails",
          component: () =>
            import("./views/components/receiptsdetails/invdmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Billing",
              },
              {
                title: "Receipts",
                url: "/receipts"
              },
              {
                title: "Receipts Detail",
                active: true
              }
            ],
            pageTitle: "Receipts Detail",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/invperiods",
          name: "invperiods",
          component: () => import("./views/components/invperiods/pemain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "Billing",
              },
              {
                title: "InvoicePeriod",
                active: true
              }
            ],
            pageTitle: "InvoicePeriod",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/zerostock",
          name: "zerostock",
          component: () => import("./views/components/zerostock/qmain.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "ตั้งค่าโปรแกรม"
              },
              {
                title: "Queue"
              },
              {
                title: "QueueZerostock",
                active: true
              }
            ],
            pageTitle: "QueueZerostock",
            rule: "masteradmin"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        },
        {
          path: "/cargowise",
          name: "cargowise",
          component: () => import("./views/components/cargowise/main.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Home",
                url: "/"
              },
              {
                title: "หน้าหลักระบบ CW1",
                active: true
              }
            ],
            pageTitle: "หน้าหลักระบบ CW1",
            rule: "user"
          },
          beforeEnter(to, from, next) {
            if (!localStorage.getItem("KK_AUTH_TOKEN")) {
              next("/pages/login");
            } else {
              next();
            }
          }
        }
      ]
    },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: "",
      component: () => import("@/layouts/full-page/FullPage.vue"),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: "/pages/authcheck/:id/:id2/:id3",
          name: "page-authcheck",
          component: () => import("@/views/pages/Authcheck.vue"),
          meta: {
            rule: "public"
          }
        },
        {
          path: "/pages/precheck",
          name: "page-precheck",
          component: () => import("@/views/pages/Precheck.vue"),
          meta: {
            rule: "public"
          }
        },
        {
          path: "/pages/login",
          name: "page-login",
          component: () => import("@/views/pages/Login.vue"),
          meta: {
            rule: "public"
          }
        },
        {
          path: "/pages/error-404",
          name: "page-error-404",
          component: () => import("@/views/pages/Error404.vue"),
          meta: {
            rule: "public"
          }
        },
        {
          path: "/pages/not-authorized",
          name: "page-not-authorized",
          component: () => import("@/views/pages/NotAuthorized.vue"),
          meta: {
            rule: "public"
          }
        },
        {
          path: "/pages/apps",
          name: "page-apps",
          component: () => import("@/views/pages/Apps.vue"),
          meta: {
            rule: "public"
          },
          beforeEnter(to, from, next) {
            const now = new Date();
            const expiredIn = new Date(localStorage.getItem("expiredIn"));

            // console.log(now)

            if (!localStorage.getItem("userRole")) {
              next("/pages/login");
            } else if (!localStorage.getItem("token")) {
              next("/pages/login");
            } else if (!expiredIn) {
              next("/pages/login");
            } else if (now >= expiredIn) {
              next("/pages/login");
            } else {
              next();
            }
          }
        }
      ]
    },
    // Redirect to 404 page, if no match found
    {
      path: "*",
      redirect: "/pages/error-404"
    }
  ]
});

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById("loading-bg");
  if (appLoading) {
    appLoading.style.display = "none";
  }
});

export default router;
